// Page: EnterpriseSignIn
// Purpose: Enterprise domain-gated sign-in. Validates email domain, optionally
// fetches enterprise by domain, and enforces email verification before access.
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { SimpleLandingHeader } from '@/components/landing/shared/SimpleLandingHeader/SimpleLandingHeader';
import useDarkMode from '../../../hooks/useDarkMode';
import AuthLoadingScreen from '../../../components/shared/AuthLoadingScreen';
import { ErrorMessage } from '../../../components/ui/feedback/error-message.jsx';
import { ButtonSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import api from '../../../api';
import SEOHead from '../../../components/shared/SEOHead';
import Footer from '../../../components/shared/Footer';

export default function EnterpriseSignIn() {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();
  const isDark = useDarkMode();
  
  // Get enterprise data from navigation state
  const enterprise = location.state?.enterprise;
  const domain = location.state?.domain;

  // If no enterprise data, try to get from domain lookup
  const [enterpriseData, setEnterpriseData] = useState(enterprise);
  const [domainData, setDomainData] = useState(domain);
  const [loadingEnterprise, setLoadingEnterprise] = useState(!enterprise);

  useEffect(() => {
    // If we don't have enterprise data, try to get it from URL or other means
    if (!enterprise && !domain) {
      // For now, redirect to regular signin with an option to enter enterprise
      navigate('/signin', { replace: true });
    } else if (!enterprise && domain) {
      // Try to fetch enterprise data by domain
      fetchEnterpriseByDomain(domain);
    } else {
      setLoadingEnterprise(false);
    }
  }, [enterprise, domain, navigate]);

  const fetchEnterpriseByDomain = async (domainToFetch) => {
    try {
      const response = await api.get(`/enterprise/check-domain/${domainToFetch}`);
      if (response.data.exists) {
        setEnterpriseData(response.data.enterprise);
        setDomainData(domainToFetch);
      } else {
        navigate('/signin', { replace: true });
      }
    } catch (err) {
      console.error('Error fetching enterprise:', err);
      navigate('/signin', { replace: true });
    } finally {
      setLoadingEnterprise(false);
    }
  };

  const toggleTheme = () => {
    if (isDark) {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    } else {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    }
  };

  const handleInputChange = () => {
    if (error) {
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    const formData = new FormData(e.target);
    const email = formData.get('email');
    const password = formData.get('password');

    // Validate email domain matches enterprise domain
    const emailDomain = email.split('@')[1]?.toLowerCase();
    if (emailDomain !== domainData.toLowerCase()) {
      setError(`Email must be from ${domainData} domain for ${enterpriseData.enterpriseName}`);
      setIsLoading(false);
      return;
    }

    try {
      // Sign in the user
      const user = await signIn(email, password);

      // Get the current Firebase user to check email verification status
      const { auth } = await import('../../../firebase/firebase');
      const firebaseUser = auth.currentUser;

      // Check if email is verified for enterprise accounts
      if (firebaseUser && !firebaseUser.emailVerified) {
        console.log('Enterprise user email not verified, redirecting to verification page');
        setIsLoading(false); // Stop loading state before navigation
        navigate('/verify-email', {
          replace: true,
          state: {
            enterprise: enterpriseData,
            isEnterprise: true,
            userEmail: email,
            userId: firebaseUser.uid
          }
        });
        return;
      }

      // If email is verified, check if user is part of this enterprise
      try {
        const response = await api.post('/enterprise/verify-user', {
          userId: user.uid || firebaseUser?.uid,
          email,
          enterpriseId: enterpriseData.id
        });

        if (!response.verified) {
          setError('You are not authorized for this enterprise account. Please contact your administrator.');
          setIsLoading(false);
          return;
        }

        console.log('Enterprise user verified, redirecting to dashboard');
        // Sign-in successful - redirect handled by RequireUnauth component or auth context
        
      } catch (verifyError) {
        console.error('Error verifying enterprise user:', verifyError);
        setError('Error verifying enterprise access. Please try again.');
        setIsLoading(false);
      }

    } catch (err) {
      console.error('Enterprise sign-in error:', err);
      
      let errorMessage = 'Failed to sign in';
      
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        errorMessage = 'Invalid email or password';
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later';
      } else if (err.message && err.message.includes('profile')) {
        errorMessage = 'Error loading your profile. Please try again.';
      }
      
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  if (loadingEnterprise) {
    return <AuthLoadingScreen />;
  }

  if (!enterpriseData) {
    return <AuthLoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
      <SEOHead pageType="enterprise-signin" />
      
      {/* Landing header with toggle theme button */}
      <SimpleLandingHeader 
        isDark={isDark} 
        toggleTheme={toggleTheme} 
        backLink="/signin"
        backButtonText="Back to Sign In"
        showLogo={false}
        enterpriseLogo={enterpriseData.enterpriseImageUrl || enterpriseData.enterpriseImage}
        enterpriseName={enterpriseData.enterpriseName}
      />
      
      {/* Content section */}
      <div className="bg-white dark:bg-gray-900 px-4 py-12 w-full">
        <div className="sm:mx-auto sm:w-full sm:max-w-md mb-8">
          <div className="text-center">
            <h2 className="text-base font-semibold leading-7 text-purple-600 dark:text-purple-400">
              Welcome back to {enterpriseData.enterpriseName}
            </h2>
            <p className="mt-2 text-4xl font-semibold tracking-tight leading-tight text-balance text-gray-900 dark:text-white sm:text-5xl sm:leading-tight max-w-[90%] md:max-w-full mx-auto">
              Sign in to your account
            </p>
            <p className="mt-4 text-lg text-gray-700 dark:text-gray-300 max-w-[90%] md:max-w-full mx-auto">
              Access your organization's {enterpriseData.planType} plan features.
            </p>
          </div>
        </div>
        
        {/* Error message */}
        {error && (
          <div className="mx-auto mb-6 w-[95%] md:w-[85%] max-w-[600px]">
            <ErrorMessage message={error} />
          </div>
        )}
        
        {/* Sign In Form */}
        <div className="mx-auto w-[95%] md:w-[85%] max-w-[600px] px-6 py-8 mt-6 sm:px-8 bg-white dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold leading-6 text-gray-900 dark:text-white">
                Work Email
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  placeholder={`your-email@${domainData}`}
                  onChange={handleInputChange}
                  className="block w-full rounded-md bg-white dark:bg-gray-700/80 border border-gray-300 dark:border-gray-600 py-2 px-3.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-500 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold leading-6 text-gray-900 dark:text-white">
                Password
              </label>
              <div className="mt-2">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  onChange={handleInputChange}
                  className="block w-full rounded-md bg-white dark:bg-gray-700/80 border border-gray-300 dark:border-gray-600 py-2 px-3.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-500 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm">
                <Link
                  to="/forgot-password"
                  className="font-medium text-purple-600 hover:text-purple-500 dark:text-purple-400 dark:hover:text-purple-300"
                >
                  Forgot your password?
                </Link>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="flex w-full justify-center items-center rounded-md bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 active:from-purple-800 active:to-indigo-800 px-3.5 py-3 text-sm font-semibold text-white shadow-md hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 transition-all duration-200 transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-95 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <ButtonSpinner color="white" className="mr-2" />
                    <span>Signing in...</span>
                  </div>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Link to enterprise sign up */}
        <div className="mx-auto w-[95%] md:w-[85%] max-w-[600px] mt-8">
          <div className="text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Need an account?
            </p>
            <Link 
              to="/enterprise/signup" 
              state={{ enterprise: enterpriseData, domain: domainData }}
              className="mt-1 inline-block font-medium text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 hover:underline"
              onClick={handleInputChange}
            >
              Create {enterpriseData.enterpriseName} account
            </Link>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
} 